import { useTranslation } from 'react-i18next'
import { Heart, Users, TrendingUp, Award, Leaf, Shield } from 'lucide-react'

const valueIcons = {
  respect: Heart,
  entrepreneurship: TrendingUp,
  loyalty: Shield,
  collaboration: Users,
  proactivity: Award,
  integrity: Leaf,
}

export function CultureSection() {
  const { t } = useTranslation('common')

  const values = [
    { key: 'respect', icon: valueIcons.respect },
    { key: 'entrepreneurship', icon: valueIcons.entrepreneurship },
    { key: 'loyalty', icon: valueIcons.loyalty },
    { key: 'collaboration', icon: valueIcons.collaboration },
    { key: 'proactivity', icon: valueIcons.proactivity },
    { key: 'integrity', icon: valueIcons.integrity },
  ]

  return (
    <section className="section-padding bg-white relative" aria-labelledby="culture-title">
      <div className="container-fiorucci">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold rounded-full mb-4">
            {t('careers.culture.badge')}
          </span>
          <h2 id="culture-title" className="heading-section text-[#4E2118] mb-6">
            {t('careers.culture.title')}
          </h2>
          <p className="text-body-lg text-[#4E2118]/70">
            {t('careers.culture.description')}
          </p>
        </div>

        {/* Facility Image */}
        <div className="relative rounded-2xl overflow-hidden mb-16 shadow-xl">
          <img
            src="/images/about/mondo_fiorucci_1970_DESKTOP.jpg"
            alt={t('careers.culture.facilityAlt')}
            className="w-full h-[300px] md:h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h3 className="text-xl md:text-2xl font-serif font-bold text-white mb-2">
                  {t('careers.culture.facilityTitle')}
                </h3>
                <p className="text-white/80">
                  {t('careers.culture.facilityLocation')}
                </p>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <span className="text-3xl font-bold">170+</span>
                <span className="text-sm">{t('careers.culture.yearsLabel')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-serif font-bold text-[#4E2118] text-center mb-10">
            {t('careers.culture.valuesTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map(({ key, icon: Icon }) => (
              <div
                key={key}
                className="group p-6 bg-[#FDF8F3] rounded-xl border border-[#F3E4D6] hover:border-[#DF631E]/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#DF631E]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#DF631E] transition-colors duration-300">
                  <Icon className="w-6 h-6 text-[#DF631E] group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="text-lg font-semibold text-[#4E2118] mb-2">
                  {t(`careers.culture.values.${key}.title`)}
                </h4>
                <p className="text-[#4E2118]/70 text-sm leading-relaxed">
                  {t(`careers.culture.values.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-[#FDF8F3] rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl font-serif font-bold text-[#4E2118] text-center mb-10">
            {t('careers.culture.benefitsTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {['growth', 'environment', 'balance', 'team'].map((key) => (
              <div key={key} className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#DF631E] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-[#4E2118] mb-1">
                    {t(`careers.culture.benefits.${key}.title`)}
                  </h4>
                  <p className="text-[#4E2118]/70 text-sm">
                    {t(`careers.culture.benefits.${key}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
